import { format, fromDto, type CurrencyCode } from '@novapet/shared/money';

/**
 * UI helper for formatting BIGINT-cents-as-string values returned by the API.
 *
 * The backend serializes Money as a `cents` string + `currency` because BIGINT can't
 * round-trip through JSON numbers. UI never multiplies/adds these strings — it just
 * formats them.
 */
export function formatCentsString(cents: string, currency: CurrencyCode = 'MXN'): string {
  return format(fromDto({ cents, currency }));
}

/**
 * Compact accounting-style rendering ("$12,345.67", no trailing currency code).
 * For dense tables (sales report columns/totals) where repeating " MXN" on every
 * cell is what pushes six-digit amounts into overlapping their neighbors.
 */
export function formatCentsCompact(cents: string, currency: CurrencyCode = 'MXN'): string {
  const money = fromDto({ cents, currency });
  const negative = money.cents < 0n;
  const abs = negative ? -money.cents : money.cents;
  const major = abs / 100n;
  const minor = abs % 100n;
  const minor_str = minor.toString().padStart(2, '0');
  const major_str = new Intl.NumberFormat('es-MX', { useGrouping: true }).format(major);
  return `${negative ? '-' : ''}$${major_str}.${minor_str}`;
}

export function centsToMajorString(cents: string): string {
  const sign = cents.startsWith('-') ? '-' : '';
  const abs = sign ? cents.slice(1) : cents;
  const padded = abs.padStart(3, '0');
  const major = padded.slice(0, padded.length - 2);
  const minor = padded.slice(padded.length - 2);
  return `${sign}${major}.${minor}`;
}

export function majorStringToCents(input: string): number {
  const trimmed = input.trim();
  if (!/^-?\d+(\.\d{1,2})?$/.test(trimmed)) throw new Error('invalid_amount');
  const negative = trimmed.startsWith('-');
  const body = negative ? trimmed.slice(1) : trimmed;
  const [whole, frac = ''] = body.split('.');
  const padded = (frac + '00').slice(0, 2);
  const cents = Number(whole + padded);
  return negative ? -cents : cents;
}
